import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, API_BASE } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { LANGS } from "@/lib/i18n";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import { FileText, Sparkles, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

function buildDefaults(tpl) {
  return Object.fromEntries(
    tpl.fields.filter((f) => f.default !== undefined).map((f) => [f.name, f.default])
  );
}

export default function Contracts() {
  const { lang } = useLang();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [fields, setFields] = useState({});
  const [outputLang, setOutputLang] = useState(lang);
  const [contract, setContract] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const selected = useMemo(() => templates.find((tt) => tt.id === selectedId) || null, [templates, selectedId]);

  const loadHistory = () => api.get("/contracts/").then(({ data }) => setHistory(data));

  useEffect(() => {
    api.get("/contracts/templates").then(({ data }) => setTemplates(data));
    loadHistory();
  }, []);

  // helpers defined at module scope

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contracts/generate", {
        template_id: selected.id,
        language: outputLang,
        fields,
      });
      setContract(data);
      loadHistory();
      toast.success("Contract generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const download = (id) => {
    window.open(`${API_BASE}/contracts/${id}/pdf`, "_blank");
  };

  const remove = async (id) => {
    await api.delete(`/contracts/${id}`);
    if (contract?.contract_id === id) setContract(null);
    loadHistory();
    toast.success("Deleted");
  };

  return (
    <AppShell>
      <div className="editorial-rule pt-4">
        <h1 className="font-display text-4xl">{t(lang,"contracts.title")}</h1>
      </div>
      <p className="mt-3 text-[#495057] max-w-2xl">{t(lang,"contracts.sub")}</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Templates list */}
        <div className="lg:col-span-4">
          <h3 className="text-xs tracking-[0.24em] uppercase font-bold text-[#868E96] mb-3">{t(lang,"contracts.pick")}</h3>
          <ul className="border border-[#E9ECEF] bg-white">
            {templates.map((tpl) => (
              <li key={tpl.id}>
                <button onClick={() => { setContract(null); setSelectedId(tpl.id); setFields(buildDefaults(tpl)); }} className={`w-full text-start p-4 border-b border-[#E9ECEF] last:border-b-0 hover:bg-[#F8F9FA] flex items-center gap-3 ${selectedId === tpl.id ? "bg-[#F8F9FA] border-s-2 border-s-[#C65D47]" : ""}`} data-testid={`template-${tpl.id}`}>
                  <FileText className="w-4 h-4 text-[#C65D47]" />
                  <span className="text-sm font-medium">{tpl[lang] || tpl.en}</span>
                </button>
              </li>
            ))}
          </ul>

          {history.length > 0 && (
            <>
              <h3 className="mt-8 text-xs tracking-[0.24em] uppercase font-bold text-[#868E96] mb-3">{t(lang,"contracts.history")}</h3>
              <ul className="border border-[#E9ECEF] bg-white divide-y divide-[#E9ECEF]" data-testid="contract-history">
                {history.map((c) => (
                  <li key={c.contract_id} className="p-3 flex items-center justify-between">
                    <button onClick={() => setContract(c)} className="flex-1 text-start" data-testid={`contract-history-${c.contract_id}`}>
                      <div className="text-sm font-medium line-clamp-1">{c.template_name}</div>
                      <div className="text-xs text-[#868E96]">{new Date(c.created_at).toLocaleDateString()} · {c.language?.toUpperCase()}</div>
                    </button>
                    <div className="flex gap-1">
                      <button onClick={() => download(c.contract_id)} className="p-1 text-[#868E96] hover:text-[#0A1128]" data-testid={`contract-download-${c.contract_id}`}><Download className="w-4 h-4" /></button>
                      <button onClick={() => remove(c.contract_id)} className="p-1 text-[#868E96] hover:text-[#C92A2A]" data-testid={`contract-delete-${c.contract_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Form / Preview */}
        <div className="lg:col-span-8">
          {!selected ? (
            <div className="bg-white border border-[#E9ECEF] p-12 text-center text-[#868E96]" data-testid="contracts-empty">
              <Sparkles className="w-8 h-8 text-[#C65D47] mx-auto" />
              <p className="mt-4">{t(lang,"contracts.pick")}</p>
            </div>
          ) : (
            <>
              <div className="bg-white border border-[#E9ECEF] p-6">
                <h2 className="font-display text-2xl mb-1">{selected[lang] || selected.en}</h2>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"contracts.output_lang")}</label>
                    <div className="mt-2 flex gap-2">
                      {LANGS.map((l) => (
                        <button type="button" key={l.code} onClick={() => setOutputLang(l.code)} data-testid={`contract-lang-${l.code}`} className={`px-3 py-1 text-xs uppercase tracking-[0.18em] border ${outputLang === l.code ? "bg-[#0A1128] text-white border-[#0A1128]" : "border-[#E9ECEF] hover:bg-[#F8F9FA]"}`}>
                          {l.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {selected.fields.map((f) => (
                    <div key={f.name}>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">
                        {(f.label?.[lang] || f.label?.en)} {f.required && <span className="text-[#C65D47]">*</span>}
                      </label>
                      {f.type === "textarea" ? (
                        <textarea
                          value={fields[f.name] ?? ""}
                          onChange={(e) => setFields((prev) => ({ ...prev, [f.name]: e.target.value }))}
                          required={f.required}
                          rows={3}
                          data-testid={`contract-field-${f.name}`}
                          className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]"
                        />
                      ) : (
                        <input
                          type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                          value={fields[f.name] ?? ""}
                          onChange={(e) => setFields((prev) => ({ ...prev, [f.name]: e.target.value }))}
                          required={f.required}
                          data-testid={`contract-field-${f.name}`}
                          className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]"
                        />
                      )}
                    </div>
                  ))}
                  <button type="submit" disabled={busy} className="bg-[#0A1128] text-white px-6 py-3 hover:bg-[#1C2538] disabled:opacity-60 inline-flex items-center gap-2" data-testid="contract-generate-btn">
                    <Sparkles className="w-4 h-4" /> {busy ? t(lang,"contracts.generating") : t(lang,"contracts.generate")}
                  </button>
                </form>
              </div>

              {contract && (
                <div className="mt-6 bg-white border border-[#E9ECEF]" data-testid="contract-preview">
                  <div className="flex items-center justify-between px-6 py-3 border-b border-[#E9ECEF]">
                    <h3 className="text-xs tracking-[0.24em] uppercase font-bold text-[#868E96]">{t(lang,"contracts.preview")}</h3>
                    <button onClick={() => download(contract.contract_id)} className="inline-flex items-center gap-2 text-sm border border-[#0A1128] px-3 py-1.5 hover:bg-[#F8F9FA]" data-testid="contract-download-btn">
                      <Download className="w-4 h-4" /> {t(lang,"contracts.download_pdf")}
                    </button>
                  </div>
                  <pre className={`p-6 text-sm whitespace-pre-wrap leading-relaxed ${contract.language === "ar" ? "text-end" : "text-start"}`} dir={contract.language === "ar" ? "rtl" : "ltr"} data-testid="contract-text">
                    {contract.content}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
